<template>
  <ak-display-table-row>
    <ak-display-table-cell>
      <template v-if="isEditing">
        <ak-select
          v-model="edit.load"
          :options="apps"
          :show-labels="false"
          :allow-empty="false"
          label="label"
          placeholder=""
          class="width-250 text-standard"
        >
          <template #singleLabel="{ option }">
            <div
              style="
                overflow: hidden;
                white-space: nowrap;
                text-overflow: ellipsis;
              "
            >
              {{ option.label }}
            </div>
          </template>
          <template #option="{ option }">
            <div
              style="white-space: pre-wrap; overflow-wrap: break-word"
              class="width-200"
            >
              {{ option.label }}
            </div>
          </template>
        </ak-select>
      </template>
      <template v-else>
        {{ title }}
        <br />
        <ak-tag :color="color">{{ text }}</ak-tag>
      </template>
    </ak-display-table-cell>
    <ak-display-table-cell class="text-capitalize">
      {{ internal.type }}
    </ak-display-table-cell>
    <ak-display-table-cell>
      <template v-if="isEditing">
        <ak-select
          v-model="edit.where"
          label="label"
          :options="params"
          :show-labels="false"
          :allow-empty="false"
          placeholder=""
          class="width-150 text-standard"
        />
      </template>
      <template v-else>{{ internal.tagKey }}</template>
    </ak-display-table-cell>
    <ak-display-table-cell>
      <template v-if="isEditing">
        <ak-field class="starts-with-field">
          <ak-input
            v-model="edit.startsWith"
            type="text"
            placeholder=""
            :custom-rules="editVariantRules"
          />
        </ak-field>
      </template>
      <template v-else>{{ internal.tagValue }}</template>
    </ak-display-table-cell>
    <ak-display-table-cell>
      <ak-switch
        v-model="internal.status"
        true-value="active"
        false-value="inactive"
      />
    </ak-display-table-cell>
    <ak-display-table-cell>
      <ak-button
        color="transparent"
        circle
        size="small"
        class="pull-right ml-tiny"
        @click="deleteVariant"
      >
        <ak-icon type="trash" color="danger" size="big" />
      </ak-button>
      <ak-button
        v-if="valid"
        color="transparent"
        circle
        size="small"
        class="pull-right ml-tiny"
        @click="toggleEdit"
      >
        <ak-icon v-if="isEditing" type="checkmark" color="success" size="big" />
        <ak-icon v-else type="android-create" color="primary" size="big" />
      </ak-button>
      <ak-button
        v-if="!valid"
        color="transparent"
        circle
        size="small"
        class="pull-right ml-tiny"
      >
        <ak-icon type="warning" color="danger" />
      </ak-button>
    </ak-display-table-cell>
  </ak-display-table-row>
</template>

<script>
import { defineComponent, ref, computed, watch, onMounted, inject } from 'vue'
import DisplayTableRow from '@akerolabs/akero-vue/src/components/displayTable/DisplayTableRow.vue'
import DisplayTableCell from '@akerolabs/akero-vue/src/components/displayTable/DisplayTableCell.vue'
import Tag from '@akerolabs/akero-vue/src/components/tag/Tag.vue'
import Field from '@akerolabs/akero-vue/src/components/field/Field.vue'
import Select from '@akerolabs/akero-vue/src/components/select/Select.vue'
import Input from '@akerolabs/akero-vue/src/components/input/Input.vue'
import Switch from '@akerolabs/akero-vue/src/components/switch/Switch.vue'
import Icon from '@akerolabs/akero-vue/src/components/icon/Icon.vue'
import Button from '@akerolabs/akero-vue/src/components/button/Button.vue'
export default defineComponent({
  name: 'VariantRow',
  components: {
    [DisplayTableRow.name]: DisplayTableRow,
    [DisplayTableCell.name]: DisplayTableCell,
    [Tag.name]: Tag,
    [Field.name]: Field,
    [Select.name]: Select,
    [Input.name]: Input,
    [Switch.name]: Switch,
    [Icon.name]: Icon,
    [Button.name]: Button,
  },
  props: {
    refPage: {
      type: String,
      default: undefined,
    },
    refExperiment: {
      type: String,
      default: undefined,
    },
    tagKey: {
      type: String,
      default: undefined,
    },
    tagValue: {
      type: String,
      default: undefined,
    },
    type: {
      type: String,
      default: undefined,
    },
    status: {
      type: String,
      default: 'active',
    },
    position: {
      type: Number,
      default: 0,
    },
    apps: {
      type: Array,
      default: () => [],
    },
    params: {
      type: Array,
      default: () => [],
    },
    validate: {
      type: Function,
      default: () => {},
    },
    valid: {
      type: Boolean,
      default: true,
    },
  },
  emits: [
    'update:refPage',
    'update:refExperiment',
    'update:tagKey',
    'update:tagValue',
    'update:type',
    'update:status',
    'delete',
  ],
  setup(props, { emit }) {
    const getPages = inject('getPages')

    const isEditing = ref(false)

    const edit = ref({
      load: null,
      where: null,
      startsWith: props.tagValue,
    })

    const internal = ref({
      refPage: props.refPage,
      refExperiment: props.refExperiment,
      tagKey: props.tagKey,
      tagValue: props.tagValue,
      type: props.type,
      status: props.status,
    })

    watch(
      internal,
      ({ refPage, refExperiment, tagKey, tagValue, type, status }) => {
        emit('update:refPage', refPage)
        emit('update:refExperiment', refExperiment)
        emit('update:tagKey', tagKey)
        emit('update:tagValue', tagValue)
        emit('update:type', type)
        emit('update:status', status)
      },
      { deep: true },
    )

    watch(
      () => props.refPage,
      (value) => {
        internal.value.refPage = value
      },
    )

    watch(
      () => props.refExperiment,
      (value) => {
        internal.value.refExperiment = value
      },
    )

    watch(
      () => props.tagKey,
      (value) => {
        internal.value.tagKey = value
      },
    )

    watch(
      () => props.tagValue,
      (value) => {
        internal.value.tagValue = value
      },
    )

    watch(
      () => props.type,
      (value) => {
        internal.value.type = value
      },
    )

    watch(
      () => props.status,
      (value) => {
        internal.value.status = value
      },
    )

    watch(
      edit,
      () => {
        props.validate()
      },
      { deep: true },
    )

    const toggleEdit = () => {
      isEditing.value = !isEditing.value
      if (!isEditing.value) {
        update()
      }
    }

    const id = computed(() => {
      return internal.value.refPage
        ? internal.value.refPage
        : internal.value.refExperiment
    })

    const selectedApp = computed(() => {
      return props.apps.find(({ value }) => value === id.value)
    })

    const selectedParam = computed(() => {
      return props.params.find(({ value }) => value === props.tagKey)
    })

    const title = computed(() => {
      const found = props.apps.find(({ value }) => value === id.value)
      if (found) return found.label
      return 'Error'
    })

    const color = computed(() => {
      const { publishedStatus, status } = selectedApp.value.document
      if (publishedStatus || status === 'active') return 'success'
      return 'grey'
    })

    const text = computed(() => {
      const { modelType, publishedStatus, status } = selectedApp.value.document
      if (modelType === 'App') {
        return publishedStatus ? 'Published' : 'Draft'
      } else if (modelType === 'Experiment') {
        return status === 'active' ? 'Active' : 'Inactive'
      }
      return 'Error'
    })

    const update = () => {
      const appDoc = edit.value.load.document
      const refId = edit.value.load.value
      const tagKey = edit.value.where.value
      const tagValue = edit.value.startsWith
      const status = internal.value.status
      let refType = 'refPage'
      let type = ''
      if (appDoc.modelType === 'App') {
        type = appDoc.type === 'embed' ? 'form' : 'page'
      } else {
        refType = 'refExperiment'
        type = 'experiment'
      }
      internal.value = {
        ...{
          refPage: undefined,
          refExperiment: undefined,
          tagKey,
          tagValue,
          type,
          status,
        },
        ...{ [refType]: refId },
      }
    }

    const editVariantRules = [
      (input) => {
        const match = getPages().find((page) => {
          if (page.position !== props.position) {
            return (
              page.tags[0].key === edit.value.where.value &&
              page.tags[0].value === edit.value.startsWith
            )
          } else {
            return false
          }
        })
        if (!match) {
          input.setCustomValidity('')
          return true
        } else {
          input.setCustomValidity('Already in use')
          return false
        }
      },
    ]

    const deleteVariant = () => {
      emit('delete', props.position)
    }

    onMounted(() => {
      edit.value.load = selectedApp.value
      edit.value.where = selectedParam.value
      edit.value.startsWith = props.tagValue
    })

    return {
      isEditing,
      toggleEdit,
      edit,
      title,
      color,
      text,
      internal,
      editVariantRules,
      deleteVariant,
    }
  },
})
</script>

<style lang="scss">
.starts-with-field {
  margin-bottom: 0 !important;
  .ak-form-error {
    margin-bottom: 0 !important;
    font-size: 85% !important;
  }
}
</style>
